'use strict'

// Constants
import {
    COLOR_ATTRIBUTE_ID,
    CATEGORY_ID_WOMEN_SWIMWEAR,
    PRODUCT_SEARCH_VARIATION_GROUP_REFINEMENT,
    CODE_MARCHE_FEM,
    TYPE_PROD_BAS,
    TYPE_PROD_HAUT,
    LIGNE_PROD_SHO,
    LIGNE_PROD_POL,
    LIGNE_PROD_CHE,
    LIGNE_PROD_MDB,
    RESEAU_H,
    IS_FAMILY_MD,
    IS_FAMILY_FS,
    IS_FAMILY_F,
    ADD_TO_CART_STRATEGIES
} from '../constants'

/**
 * Determines if a given product is a women's bikini based on specific product attributes.
 * @param {Object} product - The product object to evaluate.
 * @returns {boolean} True if the product is a women's bikini, otherwise false.
 */
export const isProductWomenBikini = (product) => {
    return (
        product &&
        product.c_orliweb_stat03_code_marche === CODE_MARCHE_FEM &&
        (product.c_orliweb_stat10_type_prod === TYPE_PROD_BAS ||
            product.c_orliweb_stat10_type_prod === TYPE_PROD_HAUT) &&
        (product.c_orliweb_code_ligne_produit === LIGNE_PROD_SHO ||
            product.c_orliweb_code_ligne_produit === LIGNE_PROD_MDB)
    )
}

/**
 * Determines if a given product is classified as men's swimshorts based on specific product attributes.
 * @param {Object} product - The product object to evaluate.
 * @param {string} product.c_orliweb_reseau - The network code of the product.
 * @param {string} product.c_orliweb_stat10_type_prod - The product type code.
 * @param {string} product.c_orliweb_code_ligne_produit - The product line code.
 * @returns {boolean} True if the product matches the criteria for men's swimshorts, otherwise false.
 */
export const isProductMenSwimshorts = (product) => {
    return (
        product &&
        product.c_orliweb_reseau === RESEAU_H &&
        product.c_orliweb_stat10_type_prod === TYPE_PROD_BAS &&
        product.c_orliweb_code_ligne_produit === LIGNE_PROD_MDB
    )
}

/**
 * Checks if a product has a cross-sell configuration enabled.
 * @param {Object} product - The product object to check for cross-sell configuration.
 * @returns {boolean} - Returns true if the product has the 'c_perfectYourLook' property set; otherwise, false.
 */
export const isThereCrossSellConfiguration = (product) => {
    return product.c_perfectYourLook
}

/**
 * Retrieves the display name of the color variation for a given product.
 *
 * This function searches through the product's variation attributes to find the color attribute
 * and then matches it with the product's current color value to return the corresponding display name.
 *
 * @param {Object} product - The product object containing variation attributes and current color value.
 * @param {Array} product.variationAttributes - An array of variation attribute objects associated with the product.
 * @param {string} product.c_color - The current color value of the product.
 * @returns {string|null} The display name of the color variation if found, otherwise null.
 */
function getColorDisplayValue(product) {
    let colorDisplayValue = null
    const variationAttributes = product?.variationAttributes || []

    const colorValues =
        variationAttributes.find(function (variationAttributeObj) {
            return variationAttributeObj.id === COLOR_ATTRIBUTE_ID
        })?.values || []

    const colorValueObj = colorValues.find(
        (colorValueObj) => colorValueObj.value === product?.c_color
    )

    if (colorValueObj) {
        colorDisplayValue = colorValueObj?.name
    }

    return colorDisplayValue
}

/**
 * Determines if a product is eligible for automatic linking based on several criteria.
 * Checks if the product is a women's bikini, does not have cross-sell configuration,
 * and has required custom attributes and a displayable color value.
 *
 * @param {Object} product - The product object to evaluate.
 * @returns {boolean} True if the product meets all eligibility criteria for automatic linking, otherwise false.
 */
export const isProductEligibleForAutomaticLinking = (product) => {
    return (
        isProductWomenBikini(product) &&
        !isThereCrossSellConfiguration(product) &&
        product?.c_orliweb_stat20_mat_rgt &&
        product?.c_color &&
        getColorDisplayValue(product)
    )
}

/**
 * Determines the appropriate recommendation strategy for a given product based on its attributes.
 *
 * The function checks the product type and specific custom attributes to select a recommendation strategy:
 * - If the product is identified as a women's bikini, returns the WOMEN_BIKINI strategy.
 * - If the product is identified as men's swimshorts, returns the MEN_SWIMSHORTS strategy.
 * - If the product's 'c_orliweb_code_ligne_produit' is "POL" or "CHE", returns the LIGNE_PRODUIT strategy.
 * - If the product's 'c_orliweb_isFamily' is "MD", "FS", or "F", returns the IS_FAMILY strategy.
 * - Otherwise, returns the DEFAULT strategy.
 *
 * @param {Object} product - The product object to evaluate, expected to contain custom attributes for strategy determination.
 * @returns {string} The selected recommendation strategy from ADD_TO_CART_STRATEGIES.
 */
export const determineRecommendationStrategy = (product) => {
    if (isProductWomenBikini(product)) {
        // apply women bikini recommendations
        return ADD_TO_CART_STRATEGIES.WOMEN_BIKINI
    } else if (isProductMenSwimshorts(product)) {
        // apply men swimshort recommendations
        return ADD_TO_CART_STRATEGIES.MEN_SWIMSHORTS
    } else if (
        product?.c_orliweb_code_ligne_produit === LIGNE_PROD_POL ||
        product?.c_orliweb_code_ligne_produit === LIGNE_PROD_CHE
    ) {
        /*
            for products with orliweb_code_ligne_produit “POL” or “CHE”, (e.g CCAC3V00-159)
            use ligne produit strategy
        */
        return ADD_TO_CART_STRATEGIES.LIGNE_PRODUIT
    } else if (
        product?.c_orliweb_isFamily === IS_FAMILY_MD ||
        product?.c_orliweb_isFamily === IS_FAMILY_FS ||
        product?.c_orliweb_isFamily === IS_FAMILY_F
    ) {
        /*
            for products with orliweb_isFamily “MD” or “FS” or “F”, (e.g BILAX461-003)
            use family strategy
        */
        return ADD_TO_CART_STRATEGIES.IS_FAMILY
    } else {
        // fallback case
        return ADD_TO_CART_STRATEGIES.DEFAULT
    }
}

/**
 * Generates an array of automatic linking refinements for a given product to be used in search queries.
 * The refinements are constructed based on the product's attributes, such as category, market code, material, color,
 * and product type (TOP or BOTTOM). Additional refinements are added to facilitate linking between complementary products.
 *
 * @param {Object} product - The product object containing custom attributes used for generating refinements.
 * @returns {string[]} An array of refinement strings for use in product search or recommendation queries.
 */
export const getAutomaticLinkingRefinements = (product) => {
    const automaticLinkingRefinements = []
    if (product) {
        automaticLinkingRefinements.push(
            `cgid=${CATEGORY_ID_WOMEN_SWIMWEAR}`, // limit the search as much as possible
            `orliweb_stat03_code_marche=${CODE_MARCHE_FEM}`,
            `orliweb_stat20_mat_rgt=${product?.c_orliweb_stat20_mat_rgt}`,
            `color=${getColorDisplayValue(product)}`,
            PRODUCT_SEARCH_VARIATION_GROUP_REFINEMENT
        )
        // If product is a TOP product
        if (product?.c_orliweb_stat10_type_prod === TYPE_PROD_HAUT) {
            automaticLinkingRefinements.push(
                `orliweb_code_ligne_produit=${LIGNE_PROD_SHO}|${LIGNE_PROD_MDB}`,
                `orliweb_stat10_type_prod=${TYPE_PROD_BAS}`
            )
        } else {
            // If product is a BOTTOM product
            automaticLinkingRefinements.push(
                `orliweb_code_ligne_produit=${LIGNE_PROD_MDB}`,
                `orliweb_stat10_type_prod=${TYPE_PROD_HAUT}`
            )
        }
    }

    return automaticLinkingRefinements
}
